import { z } from 'zod';
import {
  pagingFields,
  parseSearchParams,
  searchCount,
  type SearchFieldKinds,
  searchId,
  searchText,
  toSearchParams,
} from './search-spec.ts';

/**
 * Custom group search filter spec (Smart Search, MET-3529).
 *
 * Same contract shape as the other entity specs: the translator emits it,
 * `GET /custom-groups/filter` runs it, and it round-trips through the URL. A
 * group's meters are the members of its own sensor group, counted the same way
 * as the custom groups list (`db/sql/list-custom-groups.sql`).
 */

export const CUSTOM_GROUP_SEARCH_SORT_COLUMNS = [
  'name',
  'customerName',
  'meterCount',
  'createdAt',
] as const;

export const customGroupSearchSchema = z
  .object({
    customerId: searchId.optional().describe('Only groups belonging to this customer'),
    text: searchText.optional().describe('Free-text match against group name or address'),
    meterCountMin: searchCount.optional().describe('At least this many member meters'),
    meterCountMax: searchCount.optional().describe('At most this many member meters'),
    ...pagingFields(CUSTOM_GROUP_SEARCH_SORT_COLUMNS),
  })
  .strict()
  .refine(
    (s) =>
      s.meterCountMin === undefined || s.meterCountMax === undefined ||
      s.meterCountMin <= s.meterCountMax,
    { message: 'meterCountMin must not exceed meterCountMax', path: ['meterCountMin'] },
  );

export type CustomGroupSearch = z.infer<typeof customGroupSearchSchema>;

/** One row of `GET /custom-groups/filter`. */
export interface CustomGroupSearchRow {
  id: number;
  /** The group's own sensor group: scope Meter Search with it to see its members. */
  sensorGroupId: number;
  name: string;
  address: string | null;
  customerId: number;
  customerName: string | null;
  meterCount: number;
  createdAt: string;
}

export interface CustomGroupSearchResponse {
  customGroups: CustomGroupSearchRow[];
  total: number;
  page: number;
  pageSize: number;
}

const CUSTOM_GROUP_SEARCH_FIELDS: SearchFieldKinds = {
  customerId: 'number',
  text: 'string',
  meterCountMin: 'number',
  meterCountMax: 'number',
  page: 'number',
  pageSize: 'number',
  sortBy: 'string',
  sortDir: 'string',
};

/** Serialize a custom group search to URL params (defaults omitted; see `toSearchParams`). */
export function toCustomGroupSearchParams(search: CustomGroupSearch): URLSearchParams {
  return toSearchParams(search, CUSTOM_GROUP_SEARCH_FIELDS);
}

/** Parse URL params into a validated custom group search (see `parseSearchParams`). */
export function parseCustomGroupSearchParams(
  params: URLSearchParams,
): ReturnType<typeof customGroupSearchSchema.safeParse> {
  return parseSearchParams(customGroupSearchSchema, CUSTOM_GROUP_SEARCH_FIELDS, params);
}
